/// Port of mobile/lib/game/dino.dart.

import { RectangleHitbox } from './engine/collision';
import { SpriteAnimation, SpriteAnimationComponent } from './engine/sprite';
import type { Vec2 } from './engine/vector';
import { anchors } from './engine/vector';
import { dinoDropVelocity, dinoGravity, dinoJumpVelocity, dinoStartX, integrateJumpStep } from './gamePhysics';
import { groundLevelY } from './world';

export const dinoRunningSize: Vec2 = { x: 44, y: 47 };
export const dinoDuckingSize: Vec2 = { x: 59, y: 30 };

export type DinoState = 'idle' | 'run' | 'jump' | 'hit' | 'duck';

export class Dino extends SpriteAnimationComponent {
  isJumping = false;
  isHit = false;

  private jumpVelocity = 0.0;
  private isDuckPressed = false;
  private currentState: DinoState = 'idle';

  private hitbox: RectangleHitbox;
  private isDarkMode: boolean;

  private idleAnim!: SpriteAnimation;
  private runAnim!: SpriteAnimation;
  private duckAnim!: SpriteAnimation;
  private deadAnim!: SpriteAnimation;

  constructor(isDarkMode: boolean) {
    super({
      size: { ...dinoRunningSize },
      anchor: anchors.bottomLeft,
      position: { x: dinoStartX, y: groundLevelY },
      priority: 3,
    });

    this.isDarkMode = isDarkMode;
    this.loadAnimations();

    this.hitbox = new RectangleHitbox({
      size: { x: this.size.x * 0.5, y: this.size.y * 0.7 },
      position: { x: this.size.x * 0.25, y: this.size.y * 0.15 },
    });
    this.add(this.hitbox);

    this.applyStateVisuals(this.currentState);
  }

  get state(): DinoState {
    return this.currentState;
  }

  reloadThemeAssets(isDarkMode: boolean): void {
    this.isDarkMode = isDarkMode;
    this.loadAnimations();
    this.applyStateVisuals(this.currentState);
  }

  private loadAnimations(): void {
    const mode = this.isDarkMode ? 'dark' : 'light';

    this.idleAnim = SpriteAnimation.single(`new/dino/idle/${mode}.png`, dinoRunningSize);
    this.runAnim = SpriteAnimation.sequenced(`new/dino/run/${mode}.png`, 2, 0.15, dinoRunningSize);
    this.duckAnim = SpriteAnimation.sequenced(`new/dino/duck/${mode}.png`, 2, 0.15, dinoDuckingSize);
    this.deadAnim = SpriteAnimation.single(`new/dino/dead/${mode}.png`, dinoRunningSize);
  }

  /// Fixed-timestep jump / ground contact.
  stepPhysics(dt: number): void {
    if (this.isHit) return;

    if (this.isJumping) {
      const next = integrateJumpStep({
        y: this.y,
        velocity: this.jumpVelocity,
        gravity: dinoGravity,
        dt,
      });
      this.y = next.y;
      this.jumpVelocity = next.velocity;

      if (this.y >= groundLevelY) {
        this.land();
      }
    } else if (this.currentState !== 'duck') {
      this.y = groundLevelY;
    }
  }

  private land(): void {
    this.y = groundLevelY;
    this.isJumping = false;
    this.jumpVelocity = 0.0;
    if (this.isDuckPressed) {
      this.duck(true);
    } else {
      this.startRunning();
    }
  }

  private setState(newState: DinoState): void {
    if (this.currentState === newState) return;
    this.currentState = newState;
    this.applyStateVisuals(newState);
  }

  private applyStateVisuals(state: DinoState): void {
    switch (state) {
      case 'idle':
        this.animation = this.idleAnim;
        this.resizeTo(dinoRunningSize);
        break;
      case 'run':
        this.animation = this.runAnim;
        this.resizeTo(dinoRunningSize);
        break;
      case 'jump':
        // Jumping reuses the idle frame rather than the run cycle.
        this.animation = this.idleAnim;
        this.resizeTo(dinoRunningSize);
        break;
      case 'duck':
        this.animation = this.duckAnim;
        this.resizeTo(dinoDuckingSize);
        break;
      case 'hit':
        this.animation = this.deadAnim;
        this.resizeTo(dinoRunningSize);
        break;
    }
  }

  startRunning(): void {
    if (this.isHit) return;
    this.setState('run');
  }

  idle(): void {
    if (this.isHit) return;
    this.setState('idle');
  }

  jump(): void {
    if (this.isJumping || this.isHit) return;
    this.isJumping = true;
    this.jumpVelocity = dinoJumpVelocity;
    this.setState('jump');
  }

  duck(isDucking: boolean): void {
    this.isDuckPressed = isDucking;
    if (this.isHit) return;

    if (this.isJumping && isDucking) {
      this.jumpVelocity = dinoDropVelocity;
      return;
    }

    if (isDucking && this.y >= groundLevelY) {
      this.setState('duck');
    } else if (!isDucking) {
      this.startRunning();
    }
  }

  hit(): void {
    if (this.isHit) return;
    this.isHit = true;
    this.setState('hit');
  }

  reset(): void {
    this.isHit = false;
    this.isJumping = false;
    this.jumpVelocity = 0.0;
    this.isDuckPressed = false;
    this.currentState = 'idle';
    this.animation = this.idleAnim;
    this.resizeTo(dinoRunningSize);
    this.position = { x: dinoStartX, y: groundLevelY };
  }

  /// Dart rebuilds the hitbox component because Flame caches its geometry.
  /// Mutating in place is equivalent here and avoids add/remove churn during
  /// a state change, which can happen several times within a single frame.
  private resizeTo(newSize: Vec2): void {
    this.size = { ...newSize };
    this.hitbox.size = { x: newSize.x * 0.5, y: newSize.y * 0.7 };
    this.hitbox.position = { x: newSize.x * 0.25, y: newSize.y * 0.15 };
  }
}
